import React, { useEffect, useState, useRef, useCallback } from 'react';
import { X, AlertCircle, RefreshCw, ChevronLeft, ChevronRight } from 'lucide-react';
import moment from 'moment';
import { API } from '../../Common/Api';

// Reads the logged-in user's role/permission from localStorage.
const getViewer = () => {
  try {
    return JSON.parse(localStorage.getItem('UserDetail') || '{}') || {};
  } catch {
    return {};
  }
};
// Admin can always view; others require canViewTransactions !== false.
const canUserViewTransactions = () => {
  const u = getViewer();
  if (u?.role === 'admin') return true;
  return u?.canViewTransactions !== false;
};

const TagCostInfoModal = ({ tagRefId, onClose, isActivated = false, initialTab = 'cost' }) => {
  const [activeTab, setActiveTab] = useState(initialTab); // 'cost' | 'transactions'
  const [costData, setCostData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const modalRef = useRef(null);

  // Transactions state
  const [txns, setTxns] = useState([]);
  const [txnLoading, setTxnLoading] = useState(false);
  const [txnError, setTxnError] = useState(null);
  const [txnLoaded, setTxnLoaded] = useState(false);
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [total, setTotal] = useState(0);
  const pageSize = 10;

  // Admin ALWAYS sees the Transactions tab. For distributor/dealer/agent it is
  // shown for an activated tag when they have the "view transactions" permission.
  const isAdminViewer = getViewer()?.role === 'admin';
  const showTransactionsTab = isAdminViewer || (isActivated && canUserViewTransactions());

  const fetchCost = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const response = await API.FetchFastagCost(tagRefId);
      if (response?.success && response.data?.status === "SUC2001" && response.data.resultObj?.[0]) {
        setCostData(response.data.resultObj[0]);
      } else {
        setError(response?.message || response?.data?.message || "Failed to retrieve pricing details.");
      }
    } catch (err) {
      setError("Failed to connect to the pricing server. Please try again.");
    } finally {
      setLoading(false);
    }
  }, [tagRefId]);

  const fetchTxns = useCallback(async (pageNumber = 1) => {
    setTxnLoading(true);
    setTxnError(null);
    try {
      const res = await API.GetFastagTransactions({ fastagId: tagRefId, pageNumber, pageSize });
      if (res?.status === 200 && res?.success) {
        setTxns(Array.isArray(res.data) ? res.data : []);
        const t = res.pagination?.total || 0;
        setTotal(t);
        setTotalPages(res.pagination?.totalPages || Math.max(1, Math.ceil(t / pageSize)));
      } else {
        setTxns([]); setTotal(0); setTotalPages(1);
        setTxnError(res?.message || "Failed to fetch transactions.");
      }
    } catch (err) {
      setTxns([]); setTotal(0); setTotalPages(1);
      setTxnError("Failed to connect to the transactions server. Please try again.");
    } finally {
      setTxnLoading(false);
      setTxnLoaded(true);
    }
  }, [tagRefId]);

  useEffect(() => {
    if (tagRefId) fetchCost();
  }, [tagRefId, fetchCost]);

  // Safety: if opened straight into transactions but the viewer can't see them, fall back to cost.
  useEffect(() => {
    if (activeTab === 'transactions' && !showTransactionsTab) setActiveTab('cost');
  }, [activeTab, showTransactionsTab]);

  // Lazy-load transactions the first time the tab is opened.
  useEffect(() => {
    if (activeTab === 'transactions' && !txnLoaded) {
      fetchTxns(1);
    }
  }, [activeTab, txnLoaded, fetchTxns]);

  const goToPage = (p) => {
    if (p < 1 || p > totalPages || p === page) return;
    setPage(p);
    fetchTxns(p);
  };

  // Click outside to close
  useEffect(() => {
    const handleOutsideClick = (e) => {
      if (modalRef.current && !modalRef.current.contains(e.target)) {
        onClose();
      }
    };
    document.addEventListener('mousedown', handleOutsideClick);
    return () => document.removeEventListener('mousedown', handleOutsideClick);
  }, [onClose]);

  // Escape key to close
  useEffect(() => {
    const handleKeyDown = (e) => {
      if (e.key === 'Escape') onClose();
    };
    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [onClose]);

  const tagCost = costData?.fastagCost ?? 0;
  const securityDeposit = costData?.securityDeposit ?? 0;
  const firstRecharge = costData?.firstRechargeAmount ?? 0;
  const totalCost = tagCost + securityDeposit + firstRecharge;

  const StatusBadge = ({ status }) => {
    const s = (status || '').toString().toLowerCase();
    const cls = s.includes('succ') || s === 'success'
      ? 'bg-green-100 text-green-800'
      : s.includes('fail') || s.includes('decline')
        ? 'bg-red-100 text-red-800'
        : 'bg-yellow-100 text-yellow-800';
    return <span className={`px-2 py-[2px] rounded-full text-xs font-medium ${cls}`}>{status || 'N/A'}</span>;
  };

  return (
    <div className="fixed inset-0 bg-black/60 backdrop-blur-sm z-[999] flex justify-center items-center p-4 transition-opacity duration-300">
      <div
        ref={modalRef}
        className={`bg-white rounded-3xl shadow-2xl w-full mx-auto overflow-hidden relative transform transition-all duration-300 scale-100 border border-gray-100 ${activeTab === 'transactions' ? 'max-w-4xl' : 'max-w-md'}`}
      >
        {/* Header */}
        <div className="flex justify-between items-center px-6 pt-6 pb-4 border-b border-gray-50">
          <div>
            <h3 className="text-xl font-bold text-gray-900">FASTag Cost Heads</h3>
            <p className="text-xs text-gray-500 mt-1 select-all break-all bg-gray-50 px-2 py-1 rounded border border-gray-100 font-mono">
              Ref ID: {tagRefId}
            </p>
          </div>
          <button
            onClick={onClose}
            className="p-2 text-gray-400 hover:text-red-500 hover:bg-red-50 rounded-full transition-all duration-200"
            title="Close"
          >
            <X size={20} />
          </button>
        </div>

        {/* Tabs — shown only when a Transactions tab is available */}
        {showTransactionsTab && (
          <div className="flex gap-2 px-6 pt-4">
            <button
              onClick={() => setActiveTab('cost')}
              className={`px-4 py-2 text-sm font-semibold rounded-full transition-all ${activeTab === 'cost' ? 'bg-red-500 text-white' : 'bg-gray-100 text-gray-600 hover:bg-gray-200'}`}
            >
              Cost Heads
            </button>
            <button
              onClick={() => setActiveTab('transactions')}
              className={`px-4 py-2 text-sm font-semibold rounded-full transition-all ${activeTab === 'transactions' ? 'bg-red-500 text-white' : 'bg-gray-100 text-gray-600 hover:bg-gray-200'}`}
            >
              Transactions
            </button>
          </div>
        )}

        {/* Content */}
        <div className="p-6">
          {/* ---------- COST TAB ---------- */}
          {activeTab === 'cost' && (
            <>
              {loading && (
                <div className="flex flex-col items-center justify-center py-10 space-y-4">
                  <div className="animate-spin rounded-full h-10 w-10 border-4 border-t-red-500 border-red-200"></div>
                  <p className="text-sm font-medium text-gray-500 animate-pulse">Fetching cost details...</p>
                </div>
              )}

              {error && (
                <div className="flex flex-col items-center text-center py-6 space-y-4">
                  <div className="p-3 bg-red-50 text-red-500 rounded-full">
                    <AlertCircle size={32} />
                  </div>
                  <div className="space-y-1">
                    <h4 className="font-semibold text-gray-900">Error Fetching Prices</h4>
                    <p className="text-sm text-gray-500 max-w-xs">{error}</p>
                  </div>
                  <button
                    onClick={fetchCost}
                    className="mt-2 flex items-center gap-2 px-5 py-2 bg-red-500 hover:bg-red-600 active:bg-red-700 text-white rounded-full text-sm font-semibold transition-all shadow-md shadow-red-500/20"
                  >
                    <RefreshCw size={14} />
                    Try Again
                  </button>
                </div>
              )}

              {!loading && !error && costData && (
                <div className="space-y-6">
                  <div className="bg-gray-50/50 rounded-2xl p-4 border border-gray-100 space-y-3">
                    <div className="flex justify-between items-center py-1">
                      <span className="text-sm text-gray-500 font-medium">Tag Cost</span>
                      <span className="text-sm font-bold text-gray-800">₹{tagCost.toFixed(2)}</span>
                    </div>
                    <div className="flex justify-between items-center py-1 border-t border-gray-100">
                      <span className="text-sm text-gray-500 font-medium">Security Deposit (SD)</span>
                      <span className="text-sm font-bold text-gray-800">₹{securityDeposit.toFixed(2)}</span>
                    </div>
                    <div className="flex justify-between items-center py-1 border-t border-gray-100">
                      <span className="text-sm text-gray-500 font-medium">First Recharge Amount</span>
                      <span className="text-sm font-bold text-gray-800">₹{firstRecharge.toFixed(2)}</span>
                    </div>
                  </div>

                  <div className="bg-gradient-to-r from-red-500 to-red-600 rounded-2xl p-5 text-white shadow-lg shadow-red-500/10 flex justify-between items-center">
                    <div>
                      <p className="text-xs text-red-100 font-semibold uppercase tracking-wider">Total Value</p>
                      <p className="text-lg text-white/90 font-medium mt-0.5">Sum of all cost heads</p>
                    </div>
                    <div className="text-right">
                      <p className="text-3xl font-extrabold">₹{totalCost.toFixed(2)}</p>
                    </div>
                  </div>
                </div>
              )}
            </>
          )}

          {/* ---------- TRANSACTIONS TAB ---------- */}
          {activeTab === 'transactions' && showTransactionsTab && (
            <div>
              {txnLoading && (
                <div className="flex flex-col items-center justify-center py-10 space-y-4">
                  <div className="animate-spin rounded-full h-10 w-10 border-4 border-t-red-500 border-red-200"></div>
                  <p className="text-sm font-medium text-gray-500 animate-pulse">Fetching transactions...</p>
                </div>
              )}

              {!txnLoading && txnError && (
                <div className="flex flex-col items-center text-center py-6 space-y-4">
                  <div className="p-3 bg-red-50 text-red-500 rounded-full">
                    <AlertCircle size={32} />
                  </div>
                  <p className="text-sm text-gray-500 max-w-xs">{txnError}</p>
                  <button
                    onClick={() => fetchTxns(page)}
                    className="mt-1 flex items-center gap-2 px-5 py-2 bg-red-500 hover:bg-red-600 text-white rounded-full text-sm font-semibold transition-all"
                  >
                    <RefreshCw size={14} />
                    Try Again
                  </button>
                </div>
              )}

              {!txnLoading && !txnError && txns.length === 0 && (
                <div className="text-center py-10 text-sm text-gray-500">No transactions found for this FASTag.</div>
              )}

              {!txnLoading && !txnError && txns.length > 0 && (
                <>
                  <div className="overflow-x-auto rounded-xl border border-gray-100">
                    <table className="min-w-full text-sm text-left">
                      <thead className="bg-gray-50 text-gray-500">
                        <tr>

                          <th className="px-3 py-2 font-medium whitespace-nowrap">Vehicle No.</th>
                          <th className="px-3 py-2 font-medium whitespace-nowrap">Vehicle Class</th>
                          <th className="px-3 py-2 font-medium whitespace-nowrap">Amount</th>
                          <th className="px-3 py-2 font-medium whitespace-nowrap">Status</th>
                          <th className="px-3 py-2 font-medium whitespace-nowrap">Date</th>
                        </tr>
                      </thead>
                      <tbody className="divide-y divide-gray-100">
                        {txns.map((r, i) => (
                          <tr key={r.transactionId || i} className="hover:bg-gray-50">

                            <td className="px-3 py-2 whitespace-nowrap text-gray-700">{r.vehicleNumber || 'N/A'}</td>
                            <td className="px-3 py-2 whitespace-nowrap text-gray-700">{r.vehicleClass || 'N/A'}</td>
                            <td className="px-3 py-2 whitespace-nowrap font-semibold text-gray-800">
                              {r.amount != null ? `₹${Number(r.amount).toLocaleString('en-IN', { minimumFractionDigits: 2 })}` : 'N/A'}
                            </td>
                            <td className="px-3 py-2 whitespace-nowrap"><StatusBadge status={r.status} /></td>
                            <td className="px-3 py-2 whitespace-nowrap text-gray-600">
                              {r.transactionDate && moment(r.transactionDate).isValid()
                                ? moment(r.transactionDate).local().format('DD MMM, YYYY hh:mm A')
                                : 'N/A'}
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>

                  {/* Pagination — item range style: "1-10 of 803" */}
                  <div className="flex items-center justify-between mt-4">
                    <span className="text-xs text-gray-500">{total} transaction{total === 1 ? '' : 's'}</span>
                    <div className="flex items-center gap-3">
                      <button
                        onClick={() => goToPage(page - 1)}
                        disabled={page <= 1}
                        className="p-1 rounded-full text-gray-500 disabled:opacity-40 hover:bg-gray-100"
                      >
                        <ChevronLeft size={18} />
                      </button>
                      <span className="text-xs text-gray-600">
                        {total === 0 ? '0' : `${(page - 1) * pageSize + 1}-${Math.min(page * pageSize, total)}`} of {total}
                      </span>
                      <button
                        onClick={() => goToPage(page + 1)}
                        disabled={page >= totalPages}
                        className="p-1 rounded-full text-gray-500 disabled:opacity-40 hover:bg-gray-100"
                      >
                        <ChevronRight size={18} />
                      </button>
                    </div>
                  </div>
                </>
              )}
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="px-6 py-4 bg-gray-50 border-t border-gray-100 flex justify-end gap-2">
          <button
            onClick={onClose}
            className="px-6 py-2 bg-white border border-gray-200 hover:bg-gray-50 text-gray-700 text-sm font-semibold rounded-xl transition-all"
          >
            Close
          </button>
        </div>
      </div>
    </div>
  );
};

export default TagCostInfoModal;
